import type { PostAttachment } from '../types/board.type.ts';
import { formatFileSize } from '../common/utils.ts';

interface AttachmentListProps {
  attachments: PostAttachment[];
}

/** 확장자 추출 (배지 표시용) */
const extOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot + 1).toUpperCase() : 'FILE';
};

/**
 * 상세 페이지 첨부 파일 목록.
 *
 * download 속성으로 원본 파일명 보존을 시도하되, 교차 출처(CDN) 다운로드는
 * 서버의 Content-Disposition 헤더가 최종 결정한다(한글 파일명은 서버에서
 * filename*=UTF-8'' 인코딩 필요). 비공개 파일이면 url 대신 presigned 다운로드
 * 엔드포인트를 가리키도록 BE 에서 내려주면 된다.
 */
export function AttachmentList({ attachments }: AttachmentListProps) {
  if (attachments.length === 0) return null;

  return (
    <section className="mt-10 rounded-xl border border-black/10 dark:border-white/10 bg-neutral-50 dark:bg-dark-surface/60 p-5">
      <h2 className="mb-3 text-sm font-bold text-neutral-700 dark:text-neutral-300">
        첨부파일 {attachments.length}
      </h2>
      <ul className="space-y-2">
        {attachments.map((att) => (
          <li key={att.id}>
            <a
              href={att.url}
              download={att.fileName}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 rounded-lg bg-white dark:bg-dark-card px-4 py-2.5 ring-1 ring-black/[0.06] transition-colors hover:ring-neutral-950/30"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-neutral-950/[0.06] dark:bg-white/10 text-[10px] font-bold text-neutral-600 dark:text-neutral-300">
                {extOf(att.fileName)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-primary">
                  {att.fileName}
                </span>
                <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                  {formatFileSize(att.fileSize)}
                </span>
              </span>
              <span className="shrink-0 text-neutral-400 dark:text-neutral-500 group-hover:text-primary">
                ↓
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
